import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { Box, Typography, Popper, ClickAwayListener } from "@mui/material";
import { DateRange } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import moment from "moment";

// Inject custom styles so the calendar matches our app theme
const injectDateRangeStyles = () => {
  const finance = document.body.classList.contains("fo-mode");
  const isDark = finance && !document.body.classList.contains("fo-lite");
  const rootStyle = getComputedStyle(document.documentElement);
  const fromRoot = (name, fallback) => rootStyle.getPropertyValue(name).trim() || fallback;
  const palette = finance
    ? {
        bg: isDark ? "#160b0d" : "#fffaf9",
        surface: isDark ? "#120808" : "#faf7f6",
        text: isDark ? "#ecebf4" : "#241a2a",
        muted: isDark ? "#8f8aa6" : "#7b7189",
        accent: "#7d010c",
        range: isDark ? "rgba(192, 48, 62, 0.42)" : "rgba(125, 1, 12, 0.16)",
        border: isDark ? "rgba(255, 255, 255, 0.12)" : "rgba(125, 20, 30, 0.16)",
        hover: isDark ? "rgba(255, 255, 255, 0.08)" : "rgba(125, 1, 12, 0.06)",
        font: "'Plus Jakarta Sans', sans-serif",
      }
    : {
        bg: fromRoot("--cardBg", "#f8f9fa"),
        surface: fromRoot("--appBg", "#ffffff"),
        text: fromRoot("--secColor4", "#333333"),
        muted: fromRoot("--textSecondary", "#666666"),
        accent: fromRoot("--secColor", "#E45417"),
        range: fromRoot("--primaryColorTranslucent", "rgba(228, 84, 23, 0.28)"),
        border: fromRoot("--borderColor", "#e0e0e0"),
        hover: fromRoot("--hoverBg", "rgba(0, 0, 0, 0.05)"),
        font: "'Montserrat', sans-serif",
      };
  const styleId = "react-date-range-custom-styles";
  let styleElement = document.getElementById(styleId);

  if (!styleElement) {
    styleElement = document.createElement("style");
    styleElement.id = styleId;
    document.head.appendChild(styleElement);
  }

  styleElement.textContent = `
    .rdrDateRangeWrapper {
      background-color: ${palette.bg} !important;
      color: ${palette.text} !important;
      font-family: ${palette.font} !important;
    }
    .rdrCalendarWrapper {
      background-color: ${palette.bg} !important;
      color: ${palette.accent} !important;
      font-family: ${palette.font} !important;
      user-select: none;
      pointer-events: auto !important;
    }
    .rdrDateDisplay {
      background-color: ${palette.surface} !important;
      color: ${palette.text} !important;
      font-family: ${palette.font} !important;
    }
    .rdrDateDisplayItem {
      background-color: ${palette.bg} !important;
      color: ${palette.text} !important;
      border: 1px solid ${palette.border} !important;
      font-family: ${palette.font} !important;
    }
    .rdrDateDisplayItem input {
      color: ${palette.text} !important;
      font-family: ${palette.font} !important;
    }
    .rdrMonthAndYearWrapper {
      background-color: ${palette.surface} !important;
      color: ${palette.text} !important;
      font-family: ${palette.font} !important;
      pointer-events: auto !important;
      z-index: 5 !important;
      position: relative !important;
    }
    .rdrMonthAndYearPickers select {
      color: ${palette.text} !important;
      background-color: ${palette.bg} !important;
      font-family: ${palette.font} !important;
    }
    .rdrWeekDay {
      color: ${palette.muted} !important;
      font-family: ${palette.font} !important;
    }
    .rdrDay {
      color: ${palette.text} !important;
      font-family: ${palette.font} !important;
      cursor: pointer !important;
    }
    .rdrDayNumber span {
      color: ${palette.text} !important;
      font-family: ${palette.font} !important;
    }
    .rdrDayPassive .rdrDayNumber span {
      color: ${palette.muted} !important;
      font-family: ${palette.font} !important;
    }
    .rdrDayToday .rdrDayNumber span:after {
      background-color: ${palette.accent} !important;
    }
    .rdrSelected,
    .rdrInRange,
    .rdrStartEdge,
    .rdrEndEdge {
      background: ${palette.accent} !important;
      color: ${palette.accent} !important;
    }
    .rdrInRange {
      background: ${palette.range} !important;
      color: ${palette.range} !important;
    }
    .rdrDay:not(.rdrDayPassive) .rdrInRange ~ .rdrDayNumber span,
    .rdrDay:not(.rdrDayPassive) .rdrStartEdge ~ .rdrDayNumber span,
    .rdrDay:not(.rdrDayPassive) .rdrEndEdge ~ .rdrDayNumber span,
    .rdrDay:not(.rdrDayPassive) .rdrSelected ~ .rdrDayNumber span {
      color: #fff5f5 !important;
    }
    .rdrDayHovered {
      background-color: ${palette.hover} !important;
      font-family: ${palette.font} !important;
    }
    .rdrDayDisabled {
      color: ${palette.muted} !important;
      font-family: ${palette.font} !important;
    }
    .rdrNextPrevButton,
    .rdrPprevButton,
    .rdrNextButton {
      background-color: ${palette.bg} !important;
      color: ${palette.text} !important;
      border: 1px solid ${palette.border} !important;
      font-family: ${palette.font} !important;
      cursor: pointer !important;
      pointer-events: auto !important;
      z-index: 100 !important;
      position: relative !important;
    }
    .rdrNextPrevButton button,
    .rdrPprevButton button,
    .rdrNextButton button {
      pointer-events: auto !important;
      cursor: pointer !important;
      z-index: 101 !important;
    }
    .rdrNextPrevButton:hover,
    .rdrPprevButton:hover,
    .rdrNextButton:hover {
      background-color: ${palette.hover} !important;
    }
  `;
};

const DateRangePicker = ({
  startDate,
  endDate,
  onChange,
  fullWidth = true,
  margin = "normal",
  placeholder = "Select date range",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const anchorRef = useRef(null);
  const [focusedRange, setFocusedRange] = useState([0, 0]);
  const isUserInteractingRef = useRef(false);

  // Initialise selection range from props
  const rangeStart = startDate
    ? moment.isMoment(startDate)
      ? startDate.toDate()
      : new Date(startDate)
    : null;
  const rangeEnd = endDate
    ? moment.isMoment(endDate)
      ? endDate.toDate()
      : new Date(endDate)
    : null;

  const [selectionRange, setSelectionRange] = useState({
    startDate: rangeStart || new Date(),
    endDate: rangeEnd || rangeStart || new Date(),
    key: "selection",
  });

  useEffect(() => {
    injectDateRangeStyles();
    window.addEventListener("fo-theme", injectDateRangeStyles);
    return () => window.removeEventListener("fo-theme", injectDateRangeStyles);
  }, []);

  // Helper to compare dates by value, not reference
  const datesEqual = (date1, date2) => {
    if (!date1 && !date2) return true;
    if (!date1 || !date2) return false;
    return date1.getTime() === date2.getTime();
  };

  // Sync internal range when props change (but don't fight with user interaction)
  useEffect(() => {
    if (!isOpen && !isUserInteractingRef.current) {
      const newStart = startDate
        ? moment.isMoment(startDate)
          ? startDate.toDate()
          : new Date(startDate)
        : null;
      const newEnd = endDate
        ? moment.isMoment(endDate)
          ? endDate.toDate()
          : new Date(endDate)
        : null;

      setSelectionRange((prev) => {
        const startChanged = !datesEqual(prev.startDate, newStart);
        const endChanged = !datesEqual(prev.endDate, newEnd);

        if (startChanged || endChanged) {
          return {
            startDate: newStart || prev.startDate,
            endDate: newEnd || newStart || prev.endDate,
            key: "selection",
          };
        }
        return prev;
      });
    }
  }, [startDate, endDate, isOpen]);

  const handleSelect = useCallback(
    (ranges) => {
      const { selection } = ranges;
      isUserInteractingRef.current = true;
      setSelectionRange(selection);

      if (onChange) {
        const start = selection.startDate ? moment(selection.startDate) : null;
        const end = selection.endDate ? moment(selection.endDate) : null;

        if (start && end && start.isValid() && end.isValid()) {
          const isFirstClick = start.isSame(end, "day");

          if (isFirstClick) {
            onChange({ startDate: start, endDate: null });
          } else {
            onChange({ startDate: start, endDate: end });
            setTimeout(() => {
              setIsOpen(false);
              isUserInteractingRef.current = false;
            }, 100);
          }
        } else if (start && start.isValid()) {
          onChange({ startDate: start, endDate: null });
        }
      }

      setTimeout(() => {
        isUserInteractingRef.current = false;
      }, 300);
    },
    [onChange]
  );

  const formatDisplayDate = (date) => {
    if (!date) return "";
    const m = moment.isMoment(date) ? date : moment(date);
    return m.format("YYYY-MM-DD");
  };

  const emptyLabel = placeholder || "Select date range";

  const displayText =
    startDate && endDate
      ? `${formatDisplayDate(startDate)} - ${formatDisplayDate(endDate)}`
      : emptyLabel;

  const handleToggle = useCallback(() => {
    if (!isOpen) {
      isUserInteractingRef.current = true;
      setIsOpen(true);
    } else {
      setIsOpen(false);
      setTimeout(() => {
        isUserInteractingRef.current = false;
      }, 200);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      isUserInteractingRef.current = true;
    }
  }, [isOpen]);

  const handleClickAway = useCallback(() => {
    setIsOpen(false);
    setTimeout(() => {
      isUserInteractingRef.current = false;
    }, 100);
  }, []);

  const rangesArray = useMemo(
    () => [selectionRange],
    [
      selectionRange.startDate
        ? selectionRange.startDate.getTime()
        : null,
      selectionRange.endDate ? selectionRange.endDate.getTime() : null,
      selectionRange.key,
    ]
  );

  return (
    <Box
      sx={{
        width: fullWidth ? "100%" : "auto",
        mt: margin === "normal" ? 2 : 0,
        mb: margin === "normal" ? 1 : 0,
      }}
    >
      <Box
        className="date-range-trigger"
        ref={anchorRef}
        onClick={handleToggle}
        sx={{
          width: "100%",
          minHeight: "56px",
          px: 2,
          py: 1.5,
          border: "1px solid var(--secColor)",
          borderRadius: 0,
          color: "var(--secColor)",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          fontSize: "1rem",
          "&:hover": {
            borderColor: "var(--terColor)",
          },
        }}
      >
        <Typography
          sx={{
            color:
              displayText === emptyLabel
                ? "text.secondary"
                : "var(--secColor)",
            fontSize: "0.9rem",
          }}
        >
          {displayText}
        </Typography>
      </Box>
      <Popper
        open={isOpen}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        style={{ zIndex: 1300 }}
      >
        <ClickAwayListener onClickAway={handleClickAway}>
          <Box
            className="fo-date-pop"
            sx={{
              mt: 1,
              boxShadow: 3,
              borderRadius: 1,
              overflow: "visible",
              bgcolor: "background.paper",
            }}
          >
            <DateRange
              ranges={rangesArray}
              onChange={handleSelect}
              months={2}
              direction="horizontal"
              showDateDisplay={false}
              showMonthAndYearPickers
              moveRangeOnFirstSelection={false}
              retainEndDateOnFirstSelection={false}
              focusedRange={focusedRange}
              onRangeFocusChange={(nextFocusedRange) => {
                isUserInteractingRef.current = true;
                setFocusedRange(nextFocusedRange || [0, 0]);
                setTimeout(() => {
                  isUserInteractingRef.current = false;
                }, 100);
              }}
              minDate={new Date(1900, 0, 1)}
              maxDate={new Date(2100, 11, 31)}
            />
          </Box>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
};

export default DateRangePicker;
